// components/Pages/RM/RawMaterial/RawMaterial.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createRawMaterialAction,
    fetchAllRawMaterialAction
} from '../../../../utils/redux/rawMaterial/rawMaterialActions';
import {
    fetchRawMaterialCategories,
    fetchRawMaterialVendors,
    fetchRMCategoryProperties,
    fetchVendorItemPropertyDefinitions,
    fetchCategoryTableColumns,
    fetchRawMaterialsByCategory
} from '../../../../utils/api-service/get-services';
import DynamicFormModal from '../../../Modal/DynamicFormModal/DynamicFormModal';
import { IoIosArrowDown } from 'react-icons/io';
import { FaSearch } from 'react-icons/fa';
import { fetchAllRawMaterialVendorAction } from '../../../../utils/redux/rawMaterialVendor/rawMaterialVendorActions';

const RawMaterial = () => {
    const dispatch = useDispatch();
    const { isLoading } = useSelector(state => state.rawMaterial);
    const { rawMaterialVendors, isLoading: vendorsLoading } = useSelector(state => state.rawMaterialVendor);

    const [openRow, setOpenRow] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryConfig, setCategoryConfig] = useState(null);
    const [filteredMaterials, setFilteredMaterials] = useState([]);
    const [isDataReady, setIsDataReady] = useState(false);

    // Cascading filters
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 20;

    // Debounce search input
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Lookup map for vendors
    const vendorMap = useMemo(() => {
        const map = new Map();
        if (rawMaterialVendors && rawMaterialVendors.length) {
            rawMaterialVendors.forEach(vendor => map.set(vendor._id, vendor));
        }
        return map;
    }, [rawMaterialVendors]);

    // Lookup map for vendor property defs by vendorId + mapped column
    const propertyDefMap = useMemo(() => {
        const map = new Map();
        if (rawMaterialVendors && rawMaterialVendors.length) {
            rawMaterialVendors.forEach(vendor => {
                const vendorPropertyMap = new Map();
                if (vendor.vendorItemPropertyDefinitions) {
                    vendor.vendorItemPropertyDefinitions.forEach(def => {
                        if (def.mappedToColumn) {
                            vendorPropertyMap.set(def.mappedToColumn, def);
                        }
                    });
                }
                map.set(vendor._id, vendorPropertyMap);
            });
        }
        return map;
    }, [rawMaterialVendors]);

    // Load categories + vendors once
    useEffect(() => {
        (async () => {
            try {
                const response = await fetchRawMaterialCategories();
                const cats = response?.categories || [];
                setCategories(cats);
                if (cats.length > 0 && !selectedCategory) {
                    setSelectedCategory(cats[0]);
                }
            } catch (error) {
                console.log("Error loading categories:", error);
            }
        })();

        if (!rawMaterialVendors || !rawMaterialVendors.length) {
            dispatch(fetchAllRawMaterialVendorAction());
        }
    }, [dispatch]);

    // When category changes → load vendors, config, materials
    useEffect(() => {
        if (!selectedCategory) return;

        // Reset downstream filters
        setSelectedVendor('');
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentPage(1);

        // Load vendors for this category
        (async () => {
            try {
                const res = await fetchRawMaterialVendors("", selectedCategory._id);
                setVendors(res?.vendors || []);
            } catch (error) {
                console.error("Failed to load vendors:", error);
                setVendors([]);
            }
        })();

        // Load category config for table columns
        (async () => {
            try {
                const response = await fetchCategoryTableColumns(selectedCategory._id);
                if (response?.success) setCategoryConfig(response.data);
            } catch (error) {
                console.error("Error loading category config:", error);
            }
        })();
    }, [selectedCategory]);

    // Whenever category/vendor/search/page changes → load materials
    useEffect(() => {
        if (!selectedCategory) return;
        setIsDataReady(false);
        loadMaterials().finally(() => setIsDataReady(true));
    }, [selectedCategory, selectedVendor, debouncedSearch, currentPage]);

    // Client-side filter helper (matches server search behavior)
    const filterMaterialsClientSide = (materials, vendorId, query) => {
        let result = materials;

        // Filter by vendor
        if (vendorId) {
            result = result.filter(m => m.vendor?._id === vendorId);
        }

        // Filter by search query (name, code, vendorProperties, properties)
        if (query && query.trim()) {
            const q = query.trim().toLowerCase();
            result = result.filter(m => {
                if (m.name?.toLowerCase().includes(q)) return true;
                if (m.code?.toLowerCase().includes(q)) return true;

                // vendor properties
                if (m.vendorProperties) {
                    for (const v of Object.values(m.vendorProperties)) {
                        if (v != null && String(v).toLowerCase().includes(q)) return true;
                    }
                }
                // category properties
                if (m.properties) {
                    for (const v of Object.values(m.properties)) {
                        if (v != null && String(v).toLowerCase().includes(q)) return true;
                    }
                }
                return false;
            });
        }

        return result;
    };

    const loadMaterials = async () => {
        try {
            // Fetch all materials for the category (existing API)
            const response = await fetchRawMaterialsByCategory(selectedCategory._id);
            const allMaterials = response?.materials || [];

            // Apply vendor + search filters
            const filtered = filterMaterialsClientSide(
                allMaterials,
                selectedVendor,
                debouncedSearch
            );

            // Pagination (client-side)
            const total = filtered.length;
            const pages = Math.max(1, Math.ceil(total / itemsPerPage));
            const startIdx = (currentPage - 1) * itemsPerPage;
            const paginated = filtered.slice(startIdx, startIdx + itemsPerPage);

            setFilteredMaterials(paginated);
            setTotalItems(total);
            setTotalPages(pages);

            // If current page exceeds total pages after filter, clamp
            if (currentPage > pages && pages >= 1) {
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error loading materials:", error);
            setFilteredMaterials([]);
            setTotalItems(0);
            setTotalPages(1);
        }
    };

    // Get visible columns for the selected category
    const getCategoryColumns = useCallback(() => {
        if (!categoryConfig || !categoryConfig.displaySettings?.showVendorPropertiesInTable) {
            return [];
        }
        return (categoryConfig.tableColumns || [])
            .filter(col => col.isVisible)
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }, [categoryConfig]);

    const getDisplaySettings = () => {
        return categoryConfig?.displaySettings || {
            primaryIdentifier: "name",
            primaryVendorProperty: "",
            showVendorPropertiesInTable: true
        };
    };

    const getDisplayName = (item) => {
        const settings = getDisplaySettings();

        if (settings.primaryIdentifier === "vendorProperty" && settings.primaryVendorProperty) {
            const vendorPropertyMap = propertyDefMap.get(item.vendor?._id);
            const propertyDef = vendorPropertyMap?.get(settings.primaryVendorProperty);

            if (propertyDef && item.vendorProperties?.[propertyDef.name]) {
                return item.vendorProperties[propertyDef.name];
            }
            return item.name || 'Unnamed';
        }
        return item.name || 'Unnamed';
    };

    const getColumnValue = (item, column) => {
        if (!item.vendorProperties) return '-';
        if (!item.vendor?._id) return '-';

        const vendorPropertyMap = propertyDefMap.get(item.vendor._id);
        const propertyDef = vendorPropertyMap?.get(column.header);

        if (propertyDef && item.vendorProperties[propertyDef.name]) {
            const value = item.vendorProperties[propertyDef.name];
            if (Array.isArray(value)) return value.join(', ');
            if (typeof value === 'object') return JSON.stringify(value);
            return value;
        }
        return '-';
    };

    const handleCategoryChange = (e) => {
        const category = categories.find(c => c._id === e.target.value);
        setSelectedCategory(category);
        setOpenRow(null);
    };

    const handleVendorChange = (e) => {
        setSelectedVendor(e.target.value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleClearFilters = () => {
        setSelectedVendor('');
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentPage(1);
    };

    // ---------- RENDERING ----------
    const renderFilters = () => {
        const categorySelected = !!selectedCategory?._id;
        const isFabricCategory = selectedCategory?.slug === 'fabric';

        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        {/* 1. CATEGORY */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold">1. Category</label>
                            <select
                                className="form-select"
                                value={selectedCategory?._id || ''}
                                onChange={handleCategoryChange}
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category._id} value={category._id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* 2. VENDOR */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold">2. Vendor</label>
                            <select
                                className="form-select"
                                value={selectedVendor}
                                onChange={handleVendorChange}
                                disabled={!categorySelected}
                            >
                                <option value="">
                                    {categorySelected ? 'All Vendors' : 'Select Category first'}
                                </option>
                                {vendors.map(v => (
                                    <option key={v._id} value={v._id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 3. SEARCH */}
                        <div className="col-md-4">
                            <label className="form-label fw-bold">3. Search</label>
                            <div className="input-group">
                                <span className="input-group-text"><FaSearch /></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={
                                        !categorySelected
                                            ? 'Select Category first'
                                            : isFabricCategory
                                                ? 'Search design, shade, quality...'
                                                : 'Search by name or code...'
                                    }
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    disabled={!categorySelected}
                                />
                            </div>
                        </div>

                        {/* ACTIONS */}
                        <div className="col-12 d-flex justify-content-end">
                            <button
                                className="btn btn-secondary"
                                onClick={handleClearFilters}
                                disabled={!selectedVendor && !searchQuery}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTableHeaders = () => {
        const dynamicColumns = getCategoryColumns();
        return (
            <thead className="table-secondary">
                <tr>
                    <th className="p-3" style={{ width: '30px' }}></th>
                    <th className="p-3">Raw Material Name</th>
                    <th className="p-3">Vendor</th>
                    {dynamicColumns.map((col, idx) => (
                        <th key={idx} className="p-3">{col.header}</th>
                    ))}
                    <th className="p-3">Cost Price</th>
                    <th className="p-3">Selling Price</th>
                    <th className="p-3">Unit</th>
                </tr>
            </thead>
        );
    };

    const renderTableRow = (item, index) => {
        const categoryColumns = getCategoryColumns();
        const isOpen = openRow === index;

        return (
            <React.Fragment key={item._id}>
                <tr style={{ cursor: "pointer" }} onClick={() => setOpenRow(isOpen ? null : index)}>
                    <td className="p-3">
                        <span style={{
                            display: "inline-block",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease"
                        }}>
                            <IoIosArrowDown />
                        </span>
                    </td>
                    <td className="p-3">{getDisplayName(item)}</td>
                    <td className="p-3">{item.vendor?.name}</td>
                    {categoryColumns.map((col, colIdx) => (
                        <td key={colIdx} className="p-3">{getColumnValue(item, col)}</td>
                    ))}
                    <td className="p-3">{item.cost_price_per_unit}</td>
                    <td className="p-3">{item.selling_price_per_unit}</td>
                    <td className="p-3">{item.unit}</td>
                </tr>

                {isOpen && (
                    <tr>
                        <td colSpan={7 + categoryColumns.length} className="bg-light p-3">
                            <div className="row">
                                <div className="col-md-6">
                                    <h6 className="mb-3">📦 Material Properties</h6>
                                    {item.properties && Object.keys(item.properties).length ? (
                                        <table className="table table-sm table-bordered">
                                            <tbody>
                                                {Object.entries(item.properties).map(([key, value]) => (
                                                    <tr key={key}>
                                                        <td className="text-capitalize">{key.replace(/([A-Z])/g, ' $1')}</td>
                                                        <td>{value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="text-muted">No material properties defined</div>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <h6 className="mb-3">🏢 Vendor Properties</h6>
                                    {item.vendorProperties && Object.keys(item.vendorProperties).length ? (
                                        <table className="table table-sm table-bordered">
                                            <tbody>
                                                {Object.entries(item.vendorProperties).map(([key, value]) => (
                                                    <tr key={key}>
                                                        <td className="text-capitalize">{key.replace(/([A-Z])/g, ' $1')}</td>
                                                        <td>{value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="text-muted">No vendor properties defined</div>
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    const renderPagination = () => {
        if (!filteredMaterials || filteredMaterials.length === 0) return null;
        if (totalPages <= 1) return null;

        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {pageNumbers.map((p) => (
                        <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p)}>
                                {p}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage >= totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
                <div className="text-center text-muted small">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} -{' '}
                    {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                </div>
            </nav>
        );
    };

    const renderEmptyState = () => (
        <div className="text-center text-muted py-4">
            {isLoading
                ? "Loading..."
                : !selectedCategory
                    ? 'Select a category above to view raw materials.'
                    : `No Raw Materials found for ${selectedCategory.name} with current filters.`}
        </div>
    );

    // ---------- MODAL (unchanged from your existing code) ----------
    const [modalConfig, setModalConfig] = useState({
        show: false,
        title: "",
        fields: [],
        initialData: {},
        dropdownData: {},
        onSubmit: null
    });

    const getBaseFields = () => [
        {
            name: "name",
            label: "Item Name",
            type: "text",
            required: false,
            helpText: "Optional - For Fabric, items are identified by vendor properties"
        },
        { name: "code", label: "Item code", type: "text", required: false }
    ];

    const getPricingFields = () => [
        { name: "cost_price_per_unit", label: "Cost Price Per Unit", type: "number", required: true },
        { name: "selling_price_per_unit", label: "Selling Price Per Unit", type: "number", required: true },
        { name: "unit", label: "Unit", type: "select", required: true, optionsKey: "unit" },
        { name: "currency", label: "Currency", type: "text", defaultValue: "INR" }
    ];

    const buildMaterialDynamicFields = (materialProperties) => {
        return materialProperties.map(prop => {
            const baseField = {
                name: `properties.${prop.name}`,
                label: prop.label,
                required: prop.required || false
            };

            if (prop.type === "STRING" || prop.type === "text" || prop.type === "string") return { ...baseField, type: "text" };
            if (prop.type === "TEXTAREA" || prop.type === "textarea") return { ...baseField, type: "textarea" };
            if (prop.type === "NUMBER" || prop.type === "number") return { ...baseField, type: "number" };
            if (prop.type === "DATE" || prop.type === "date") return { ...baseField, type: "date" };
            if (prop.type === "BOOLEAN" || prop.type === "boolean") return { ...baseField, type: "checkbox" };
            if (prop.type === "SELECT" || prop.type === "select") return { ...baseField, type: "select", optionsKey: `prop_${prop.name}` };
            if (prop.type === "MULTISELECT" || prop.type === "multiselect") return { ...baseField, type: "multiselect", optionsKey: `prop_${prop.name}` };
            return baseField;
        });
    };

    const buildPropertyDropdowns = (materialProperties) => {
        const dropdowns = {};
        materialProperties.forEach(prop => {
            if (prop.type === "SELECT" || prop.type === "select" || prop.type === "MULTISELECT" || prop.type === "multiselect") {
                dropdowns[`prop_${prop.name}`] =
                    prop.options?.map(opt => ({ _id: opt.value, name: opt.label })) || [];
            }
        });
        return dropdowns;
    };

    const buildVendorFieldsAndDropdowns = (vendorProperties) => {
        const fields = vendorProperties.map(prop => {
            const baseField = {
                name: `vendorProperties.${prop.name}`,
                label: `🏢 ${prop.label}`,
                required: prop.required || false,
                placeholder: prop.placeholder || `Enter ${prop.label.toLowerCase()}`
            };
            if (prop.type === "STRING" || prop.type === "string") return { ...baseField, type: "text" };
            if (prop.type === "TEXTAREA" || prop.type === "textarea") return { ...baseField, type: "textarea" };
            if (prop.type === "NUMBER" || prop.type === "number") return { ...baseField, type: "number" };
            if (prop.type === "DATE" || prop.type === "date") return { ...baseField, type: "date" };
            if (prop.type === "BOOLEAN" || prop.type === "boolean") return { ...baseField, type: "checkbox" };
            if (prop.type === "SELECT" || prop.type === "select") return { ...baseField, type: "select", optionsKey: `vendor_prop_${prop.name}` };
            if (prop.type === "MULTISELECT" || prop.type === "multiselect") return { ...baseField, type: "multiselect", optionsKey: `vendor_prop_${prop.name}` };
            return baseField;
        });

        const dropdowns = {};
        vendorProperties.forEach(prop => {
            if (prop.type === "SELECT" || prop.type === "select" || prop.type === "MULTISELECT" || prop.type === "multiselect") {
                dropdowns[`vendor_prop_${prop.name}`] =
                    prop.options?.map(opt => ({ _id: opt.value, name: opt.label })) || [];
            }
        });

        return { fields, dropdowns };
    };

    const rebuildFormForCategory = async (categoryId) => {
        const vendorRes = await fetchRawMaterialVendors("", categoryId);
        const vendorsList = vendorRes?.vendors || [];

        const propRes = await fetchRMCategoryProperties(categoryId);
        const materialProperties = propRes?.propertyDefinitions || [];
        const materialDynamicFields = buildMaterialDynamicFields(materialProperties);
        const propertyDropdowns = buildPropertyDropdowns(materialProperties);

        setModalConfig(prev => ({
            ...prev,
            fields: [
                ...getBaseFields(),
                {
                    name: "category",
                    label: "Category",
                    type: "select",
                    required: true,
                    optionsKey: "categories",
                    value: categoryId,
                    onChange: (value) => rebuildFormForCategory(value)
                },
                {
                    name: "vendor",
                    label: "Vendor",
                    type: "select",
                    optionsKey: "vendors",
                    onChange: async (vendorId) => {
                        if (!vendorId) return;
                        try {
                            const vendorPropRes = await fetchVendorItemPropertyDefinitions(vendorId);
                            const vendorProperties = vendorPropRes?.propertyDefinitions || [];
                            const { fields: vendorFields, dropdowns: vendorDropdowns } =
                                buildVendorFieldsAndDropdowns(vendorProperties);

                            setModalConfig(prevConfig => {
                                const fieldsWithoutVendorProps = prevConfig.fields.filter(
                                    f => !f.name.startsWith("vendorProperties.")
                                );
                                const vendorFieldIndex = fieldsWithoutVendorProps.findIndex(f => f.name === "vendor");
                                const updatedFields = [...fieldsWithoutVendorProps];
                                if (vendorFieldIndex !== -1) {
                                    updatedFields.splice(vendorFieldIndex + 1, 0, ...vendorFields);
                                }
                                return {
                                    ...prevConfig,
                                    fields: updatedFields,
                                    dropdownData: {
                                        ...prevConfig.dropdownData,
                                        ...vendorDropdowns
                                    }
                                };
                            });
                        } catch (error) {
                            console.error("Error fetching vendor property definitions:", error);
                        }
                    }
                },
                ...materialDynamicFields,
                ...getPricingFields()
            ],
            dropdownData: {
                ...prev.dropdownData,
                categories,
                vendors: vendorsList,
                unit: [
                    { _id: "PCS", name: "PCS" },
                    { _id: "KG", name: "KG" },
                    { _id: "METER", name: "METER" },
                    { _id: "STITCH", name: "STITCH" },
                    { _id: "HOUR", name: "HOUR" },
                    { _id: "FT", name: "FT" }
                ],
                ...propertyDropdowns
            },
            initialData: {
                ...prev.initialData,
                category: categoryId,
                properties: {},
                vendorProperties: {}
            }
        }));
    };

    const openAddModal = async () => {
        const initialVendors = selectedCategory
            ? await fetchRawMaterialVendors("", selectedCategory._id)
            : { vendors: [] };

        const initialMaterialProps = selectedCategory
            ? await fetchRMCategoryProperties(selectedCategory._id)
            : { propertyDefinitions: [] };

        const materialProperties = initialMaterialProps.propertyDefinitions || [];
        const materialDynamicFields = buildMaterialDynamicFields(materialProperties);
        const propertyDropdowns = buildPropertyDropdowns(materialProperties);

        setModalConfig({
            show: true,
            title: "Add Raw Material",
            fields: [
                ...getBaseFields(),
                {
                    name: "category",
                    label: "Category",
                    type: "select",
                    required: true,
                    optionsKey: "categories",
                    value: selectedCategory?._id || "",
                    onChange: (value) => rebuildFormForCategory(value)
                },
                {
                    name: "vendor",
                    label: "Vendor",
                    type: "select",
                    optionsKey: "vendors",
                    onChange: async (vendorId) => {
                        if (!vendorId) return;
                        try {
                            const vendorPropRes = await fetchVendorItemPropertyDefinitions(vendorId);
                            const vendorProperties = vendorPropRes?.propertyDefinitions || [];
                            const { fields: vendorFields, dropdowns: vendorDropdowns } =
                                buildVendorFieldsAndDropdowns(vendorProperties);

                            setModalConfig(prevConfig => {
                                const fieldsWithoutVendorProps = prevConfig.fields.filter(
                                    f => !f.name.startsWith("vendorProperties.")
                                );
                                const vendorFieldIndex = fieldsWithoutVendorProps.findIndex(f => f.name === "vendor");
                                const updatedFields = [...fieldsWithoutVendorProps];
                                if (vendorFieldIndex !== -1) {
                                    updatedFields.splice(vendorFieldIndex + 1, 0, ...vendorFields);
                                }
                                return {
                                    ...prevConfig,
                                    fields: updatedFields,
                                    dropdownData: {
                                        ...prevConfig.dropdownData,
                                        ...vendorDropdowns
                                    }
                                };
                            });
                        } catch (error) {
                            console.error("Error fetching vendor property definitions:", error);
                        }
                    }
                },
                ...materialDynamicFields,
                ...getPricingFields()
            ],
            initialData: {
                currency: "INR",
                category: selectedCategory?._id || "",
                properties: {},
                vendorProperties: {}
            },
            dropdownData: {
                categories,
                vendors: initialVendors.vendors || [],
                unit: [
                    { _id: "PCS", name: "PCS" },
                    { _id: "KG", name: "KG" },
                    { _id: "METER", name: "METER" },
                    { _id: "STITCH", name: "STITCH" },
                    { _id: "HOUR", name: "HOUR" },
                    { _id: "FT", name: "FT" }
                ],
                ...propertyDropdowns
            },
            onSubmit: async (data) => {
                const payload = {
                    name: data.name,
                    code: data.code,
                    category: data.category,
                    vendor: data.vendor,
                    cost_price_per_unit: parseFloat(data.cost_price_per_unit),
                    selling_price_per_unit: parseFloat(data.selling_price_per_unit),
                    unit: data.unit,
                    currency: data.currency,
                    properties: data.properties || {},
                    vendorProperties: data.vendorProperties || {}
                };

                await dispatch(createRawMaterialAction(payload));
                await loadMaterials();
                setModalConfig(prev => ({ ...prev, show: false }));
            }
        });
    };

    // Initial loading gate
    if (vendorsLoading || !isDataReady) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading raw materials...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex flex-column align-items-center">
                <div className="d-flex my-4 w-100 justify-content-between align-items-center">
                    <h3>Raw Material {selectedCategory ? `- ${selectedCategory.name}` : ''}</h3>
                    <button onClick={openAddModal} className="btn btn-primary">
                        + Add Raw Material
                    </button>
                </div>

                <div className="w-100">
                    {renderFilters()}

                    <div className="table-responsive">
                        <table className="table table-sm align-middle mt-3">
                            {renderTableHeaders()}
                            <tbody>
                                {filteredMaterials?.length ? (
                                    filteredMaterials.map((item, index) => renderTableRow(item, index))
                                ) : (
                                    <tr>
                                        <td colSpan={7 + getCategoryColumns().length} className="text-center">
                                            {renderEmptyState()}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </div>
            </div>

            <DynamicFormModal
                show={modalConfig.show}
                title={modalConfig.title}
                fields={modalConfig.fields}
                initialData={modalConfig.initialData}
                onSubmit={modalConfig.onSubmit}
                dropdownData={modalConfig.dropdownData}
                onHide={() => setModalConfig(prev => ({ ...prev, show: false }))}
            />
        </>
    );
};

export default RawMaterial;